import React, { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import { Card, CardHeader, CardBody, CardTitle, Row, Col, Button, ButtonGroup, FormGroup, Input, Label } from 'reactstrap';
import { getCookie } from './utils';
import { METRICS, TOP_N, ALL, formatDate, formatDuration, cellsFor, sportsOf, defaultSport, topRows } from './effortMetrics';

Chart.register(LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend);

const resource_api_url = process.env.REACT_APP_RESOURCE_API_URL;

// One chart per effort: the TOP_N best activities of the chosen sport, plotted over time.
// The Top 10 metrics come first; the three older whole-run measures follow.
const CHARTS = [
  ...METRICS.map((m) => ({ ...m, color: m.field.startsWith('Consistent') ? '#00d6b4' : '#1f8ef1' })),
  { field: 'FullRunConsistencyStdDev', label: 'Full run consistency', unit: 'km/h std dev', higherIsBetter: false, pace: false,
    note: 'Lower = more consistent', color: '#00d6b4' },
  { field: 'Qrt50Speed', label: 'Cruising speed (50th pct)',   unit: 'km/h', higherIsBetter: true, pace: true, color: '#fd5d93' },
  { field: 'Qrt75Speed', label: 'Power zone speed (75th pct)', unit: 'km/h', higherIsBetter: true, pace: true, color: '#fd5d93' },
];

const SURFACE = '#1f2233';      // the card colour: a 2px ring in it keeps overlapping dots apart
const TEXT_MUTED = '#9a9a9a';
const GRID = 'rgba(255,255,255,0.05)';
const GRID_X = 'rgba(255,255,255,0.14)';
const Y_AXIS_WIDTH = 72;

const toTime = (iso) => new Date(iso).getTime();
const MS_PER_YEAR = 365.25 * 86400000;
const MAX_YEAR_LABELS = 10;

// The time axis. Over two years or more it shows calendar years and leaves out the years in which the sport has no
// activity at all (every year with data gets the same width, so a pause does not eat up half the chart);
// under two years it is a plain date axis with a tick per month.
// -> { toX(ms), min, max, ticks: [{ value, label }], title }
function buildAxis(times) {
  const lo = Math.min(...times);
  const hi = Math.max(...times);
  if (hi - lo < 2 * MS_PER_YEAR) {
    const ticks = [];
    for (let d = new Date(new Date(lo).getFullYear(), new Date(lo).getMonth() + 1, 1); d.getTime() <= hi; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
      ticks.push({ value: d.getTime(), label: d.getMonth() === 0 ? String(d.getFullYear()) : d.toLocaleDateString('en-GB', { month: 'short' }) });
    }
    const pad = (hi - lo) * 0.03 || 86400000;
    const months = Math.max(1, Math.round((hi - lo) / MS_PER_YEAR * 12));
    return { toX: (ms) => ms, min: lo - pad, max: hi + pad, ticks, title: `${new Date(lo).getFullYear()} – ${new Date(hi).getFullYear()} · ${months} months` };
  }
  const years = Array.from(new Set(times.map((t) => new Date(t).getFullYear()))).sort((x, y) => x - y);
  const slot = new Map(years.map((y, i) => [y, i]));
  const toX = (ms) => {
    const y = new Date(ms).getFullYear();
    const start = new Date(y, 0, 1).getTime();
    return slot.get(y) + (ms - start) / (new Date(y + 1, 0, 1).getTime() - start);
  };
  const step = Math.max(1, Math.ceil(years.length / MAX_YEAR_LABELS));
  const ticks = years.map((y, i) => ({ value: i, label: String(y) })).filter((_, i) => i % step === 0);
  const gaps = years[years.length - 1] - years[0] + 1 - years.length;
  const span = `${years[0]} – ${years[years.length - 1]} · ${years.length} ${years.length === 1 ? 'year' : 'years'} with data`;
  return { toX, min: -0.03 * years.length, max: years.length * 1.03, ticks, title: gaps > 0 ? `${span} (${gaps} without activities left out)` : span };
}

function makeOptions(metric, points, axis) {
  const best = metric.higherIsBetter;
  return {
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: true },
    onHover: (evt, elements) => {
      if (evt.native) evt.native.target.style.cursor = elements.length ? 'pointer' : 'default';
    },
    onClick: (evt, elements) => {
      if (elements.length) {
        window.open(`https://www.strava.com/activities/${points[elements[0].index].row.ActivityId}`, '_blank', 'noopener');
      }
    },
    plugins: {
      legend: { display: false }, // one series: the card title names it
      tooltip: {
        callbacks: {
          title: (items) => {
            const p = points[items[0].dataIndex];
            return `#${p.rank} · ${p.row.ActivityName || p.row.ActivityId}`;
          },
          label: (ctx) => {
            const p = points[ctx.dataIndex];
            const cells = cellsFor(metric, p.y);
            return [
              metric.distanceKm ? ` ${cells.main}` : ` ${cells.main} ${metric.unit}`,
              ...(cells.pace ? [` ${cells.pace} /km`] : []),
              ` ${formatDate(p.row.ActivityDate)}`,
            ];
          },
          footer: () => 'Click to open in Strava',
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        min: axis.min,
        max: axis.max,
        afterBuildTicks: (scale) => {
          scale.ticks = axis.ticks.map(({ value }) => ({ value }));
        },
        ticks: {
          color: TEXT_MUTED,
          font: { size: 12 },
          autoSkip: false,
          maxRotation: 0,
          callback: (v) => (axis.ticks.find((t) => t.value === v) || {}).label,
        },
        grid: { color: GRID_X }, // the year lines are the point of this axis, so they are stronger than the y grid
        title: { display: true, text: axis.title, color: TEXT_MUTED, font: { size: 11 } },
      },
      y: {
        // Same width for every chart, whatever its labels ('60' or '1:30:00'): the plot areas then start and end at the
        // same place, so the year columns line up from one chart to the next.
        afterFit: (scale) => {
          scale.width = Y_AXIS_WIDTH;
        },
        reverse: !best, // better is always up
        grace: '10%',
        ticks: {
          color: TEXT_MUTED,
          font: { size: 11 },
          callback: (v) => (metric.distanceKm ? formatDuration(v) : v),
        },
        grid: { color: GRID },
        title: { display: true, text: metric.unit, color: TEXT_MUTED, font: { size: 11 } },
      },
    },
  };
}

function buildChart(history, metric, sport) {
  const top = topRows(history, metric, sport, TOP_N);
  if (top.length === 0) return null;
  // rank = position in the top list; dots sit at their date, #1 is drawn larger
  const points = top
    .filter((r) => r.ActivityDate)
    .map((r, i) => ({ ms: toTime(r.ActivityDate), y: r.value, rank: i + 1, row: r }));
  if (points.length === 0) return null;
  // the time axis spans all activities of the sport, so you see how recent or old the best results are
  const times = history.filter((r) => r.ActivityDate && (sport === ALL || r.SportType === sport)).map((r) => toTime(r.ActivityDate));
  const axis = buildAxis(times);
  points.forEach((p) => { p.x = axis.toX(p.ms); });
  return {
    points,
    axis,
    data: {
      datasets: [
        {
          data: points,
          showLine: false,
          backgroundColor: metric.color,
          borderColor: SURFACE,
          borderWidth: 2,
          pointRadius: points.map((p) => (p.rank === 1 ? 9 : 6)),
          pointHoverRadius: points.map((p) => (p.rank === 1 ? 11 : 8)),
          hitRadius: 14,
        },
      ],
    },
  };
}

// ── component ─────────────────────────────────────────────────────────────────
function History() {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [sportChoice, setSportChoice] = useState(null); // null = not chosen yet, use the default
  const [hidden, setHidden] = useState(() => new Set()); // charts that are unchecked; empty = all shown

  useEffect(() => {
    const token = getCookie('stratava_token');
    if (!token) {
      setError('Not authenticated. Please sign in on the Activities page first.');
      return;
    }

    axios
      .get(`${resource_api_url}/api/Get_Activity_History`, {
        headers: { Authorization: token },
      })
      .then((res) => setHistory(res.data))
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 440) {
          setError('Session expired. Please sign in again on the Activities page.');
        } else {
          setError('Failed to load history. Please try again later.');
        }
      });
  }, []);

  const sports = useMemo(() => sportsOf(history), [history]);
  const sport = sportChoice || defaultSport(sports);
  const charts = useMemo(
    () => (history ? CHARTS.map((m) => ({ metric: m, chart: buildChart(history, m, sport) })).filter((c) => c.chart) : []),
    [history, sport]
  );
  const visible = charts.filter(({ metric }) => !hidden.has(metric.field));

  const toggle = (field) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });

  // ── loading / error states ─────────────────────────────────────────────────
  if (error) {
    return (
      <div className="content">
        <div className="forge-auth-panel">
          <div className="forge-subhead" style={{ marginBottom: '12px' }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px', color: 'var(--forge-warning)' }} />
            HISTORY UNAVAILABLE
          </div>
          <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!history) {
    return (
      <div className="content">
        <div className="forge-status-msg">
          <i className="fas fa-circle-notch fa-spin" style={{ color: 'var(--forge-primary)', marginRight: '12px' }} />
          Loading your performance history...
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="content">
        <div className="forge-auth-panel">
          <div className="forge-subhead" style={{ marginBottom: '12px' }}>
            <i className="fas fa-chart-line" style={{ marginRight: '8px', color: 'var(--forge-primary)' }} />
            NO HISTORY YET
          </div>
          <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)' }}>
            Analyse some activities on the Activities page and your results will appear here.
          </p>
        </div>
      </div>
    );
  }

  // ── render charts ──────────────────────────────────────────────────────────
  return (
    <div className="content">
      <div style={{ marginBottom: '24px' }}>
        <div className="forge-headline" style={{ marginBottom: '4px' }}>
          <i className="fas fa-chart-line" style={{ marginRight: '10px', color: 'var(--forge-primary)', fontSize: '1.25rem' }} />
          PERFORMANCE HISTORY
        </div>
        <div className="forge-label" style={{ opacity: 0.6 }}>
          Your {TOP_N} best results per measure, over time · {history.length} {history.length === 1 ? 'activity' : 'activities'} analysed
        </div>
      </div>

      {sports.length > 2 && (
        <ButtonGroup style={{ flexWrap: 'wrap', marginBottom: '24px' }}>
          {sports.map((s) => (
            <Button key={s} color="primary" size="sm" outline={s !== sport} onClick={() => setSportChoice(s)}>
              {s}
            </Button>
          ))}
        </ButtonGroup>
      )}

      {charts.length === 0 && (
        <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)' }}>No results for {sport} yet.</p>
      )}

      {charts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '8px', marginBottom: '24px' }}>
          <span className="forge-label" style={{ opacity: 0.6, marginRight: '8px' }}>SHOW</span>
          {charts.map(({ metric }) => (
            <FormGroup check inline key={metric.field} style={{ marginBottom: 0 }}>
              <Label check>
                <Input type="checkbox" checked={!hidden.has(metric.field)} onChange={() => toggle(metric.field)} />
                {metric.label}
                <span className="form-check-sign">
                  <span className="check" />
                </span>
              </Label>
            </FormGroup>
          ))}
          <Button color="link" size="sm" onClick={() => setHidden(new Set())}>All</Button>
          <Button color="link" size="sm" onClick={() => setHidden(new Set(charts.map(({ metric }) => metric.field)))}>None</Button>
        </div>
      )}

      {charts.length > 0 && visible.length === 0 && (
        <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)' }}>Select at least one measure above.</p>
      )}

      <Row>
        {visible.map(({ metric, chart }) => {
          const best = chart.points[0];
          const bestCells = cellsFor(metric, best.y);
          return (
            <Col lg="6" key={metric.field} style={{ marginBottom: '24px' }}>
              <Card className="card-chart">
                <CardHeader>
                  <CardTitle tag="h4" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {metric.label}
                  </CardTitle>
                  <p className="card-category" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                    {metric.note ? `${metric.note} · ` : ''}Best: {bestCells.main}{metric.distanceKm ? '' : ` ${metric.unit}`} on {formatDate(best.row.ActivityDate)}
                  </p>
                </CardHeader>
                <CardBody>
                  <div className="chart-area" style={{ height: '260px' }}>
                    <Line data={chart.data} options={makeOptions(metric, chart.points, chart.axis)} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}

export default History;
